import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { ChangeEvent,useEffect,useState } from "react";
import { trackEventMatomo } from "../../helpers/matomo";
import { ClientsWithChildren } from "../../types/clientes/types";
import type { cliente,conjunto,local } from "../../types/generic-acordeon-places/types";
import usePagination from "../Pagination/Pagination";

type props = {
  isLoading: boolean
  clients: ClientsWithChildren[]
  conjuntosArray: string[],
  setConjuntosArray: Function
};

export default function GenericAcordonPlaces({
  isLoading,
  clients,
  conjuntosArray,
  setConjuntosArray
}: props) {
  const [search, setSearch] = useState<string>('');
  const [paginated, setPaginated] = useState<cliente[]>([]);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChangeExpand = (isExpanded: boolean, panel: string) => {
    setExpanded(isExpanded ? panel : false);
  };

  const PER_PAGE = 5;
  const count = Math.ceil(clients?.length / PER_PAGE);
  const _DATA = usePagination(clients?.sort((a: cliente, b: cliente) => { return a.nome > b.nome ? 1 : -1; }), PER_PAGE);

  useEffect(() => {
    search ? setPaginated(clients
      ?.filter((e: cliente) => e.nome
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(search
          ?.normalize("NFD")
          ?.replace(/[\u0300-\u036f]/g, "")
          ?.toLowerCase()))) : setPaginated(_DATA.currentData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, isLoading]);

  const handleChange = (_e: ChangeEvent<unknown>, p: number) => {
    setPage(p);
    _DATA.jump(p);
  };

  return (
    isLoading
      ? <Stack
        width={'100%'}
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2}
      >
        <Stack
          width={'100%'}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Skeleton variant="rounded" height={40} width={'15%'} />
          <Skeleton variant="rounded" height={40} width={'80%'} />
        </Stack>
        <Skeleton variant="rounded" height={60} width={'100%'} />
      </Stack>
      : !clients?.length
        ? <Card sx={{ border: '1px solid orange' }}>
          <CardContent>
            <Typography color='GrayText' variant='subtitle1' textAlign='justify'>
              * Você não possui nenhuma estrutura geográfica
              associada a esse tipo,
              por favor associe em Gestão de
              Serviços na aba de Gestão de Local,
              ou em Gestão de Cliente na aba Meus Clientes.
            </Typography>
          </CardContent>
        </Card>
        : <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='center' spacing={1}>
          <SearchInput search={search} setSearch={setSearch} arrayLenght={clients?.length} name={'Clientes:'} />
          {paginated
            ?.sort((a: cliente, b: cliente) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
            ?.map((client: cliente, index: number) => (
              <ClientsAcordion
                key={client.id}
                conjuntosArray={conjuntosArray}
                setConjuntosArray={setConjuntosArray}
                expanded={expanded}
                setExpanded={handleChangeExpand}
                client={client}
              />
            ))}
          {(clients?.length > 5 && !search)
            && <Pagination
              boundaryCount={4}
              showFirstButton
              showLastButton
              count={count}
              size="small"
              page={page}
              variant='text'
              shape='circular'
              onChange={handleChange}
            />}
        </Stack>
  );
}



const ClientsAcordion = ({
  client,
  expanded,
  setExpanded,
  conjuntosArray,
  setConjuntosArray,
}: {
  client: cliente,
  expanded: boolean | string,
  setExpanded: Function,
  conjuntosArray: string[]
  setConjuntosArray: Function
}) => {
  const [search, setSearch] = useState<string>('');
  const [paginated, setPaginated] = useState<local[]>([]);
  const [page, setPage] = useState(1);

  const [expandedLocal, setExpandedLocal] = useState<string | false>(false);

  const handleChangeExpand = (isExpanded: boolean, panel: string) => {
    setExpandedLocal(isExpanded ? panel : false);
  };

  const PER_PAGE = 5;
  const count = Math.ceil(client.locaisList?.length / PER_PAGE);
  const _DATA = usePagination(client.locaisList?.sort((a: local, b: local) => { return a.nome > b.nome ? 1 : -1; }), PER_PAGE);

  useEffect(() => {
    search ? setPaginated(client.locaisList
      ?.filter((e: local) => e.nome
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(search
          ?.normalize("NFD")
          ?.replace(/[\u0300-\u036f]/g, "")
          ?.toLowerCase()))) : setPaginated(_DATA.currentData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  const handleChange = (_e: ChangeEvent<unknown>, p: number) => {
    setPage(p);
    _DATA.jump(p);
  };

  const allSetsFromClient: string[] = [];
  client?.locaisList?.forEach((local: local) => {
    if (local?.conjuntosList?.length) {
      local?.conjuntosList?.forEach((cnj: conjunto) => allSetsFromClient?.push(cnj.id));
    } else allSetsFromClient?.push('Este local não tem conjuntos');
  });

  function handleChangeClick(event: ChangeEvent<HTMLInputElement>) {
    const cnjs: string[] = [];
    if (event.target.checked) {
      client.locaisList?.forEach((local) => {
        local.conjuntosList?.forEach((cnj) => {
          setConjuntosArray((prev: string[]) => prev?.length ? [...prev, cnj.id] : [cnj.id]);
          cnjs?.push(cnj.id);
        });
      });
    }
    else {
      client.locaisList?.forEach((local) => {
        local.conjuntosList?.forEach((cnj) => {
          setConjuntosArray((prev: string[]) => prev?.filter((el: string) => el !== cnj.id));
          cnjs.push(cnj.id);
        });
      });
    }
  }

  return (
    <>
      <Accordion
        key={client.id}
        sx={{ width: '100%' }}
        expanded={expanded === client.id}
      >
        <AccordionSummary
          expandIcon={<ArrowDownwardIcon
            onClick={(isExpanded) => {
              trackEventMatomo('Gestão de formulários', 'click', 'accordeon', 'abre/fecha');
              if (expanded === client.id) {
                setExpanded(!isExpanded, '');
              } else
                setExpanded(true, client.id);
            }} />}
        >
          <Typography variant="h6">
            <FormGroup>
              <FormControlLabel
                label={client.nome}
                control={
                  <Checkbox
                    checked={allSetsFromClient?.every(set => conjuntosArray?.includes(set)) ?? false}
                    indeterminate={allSetsFromClient?.some(set => conjuntosArray
                      ?.includes(set))
                      && !allSetsFromClient?.every(set => conjuntosArray?.includes(set))}
                    onChange={handleChangeClick}
                  />
                }
              />
            </FormGroup>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='center' spacing={1}>
            <SearchInput search={search} setSearch={setSearch} arrayLenght={client.locaisList?.length} name={'Locais:'} />
            {paginated
              ?.sort((a: local, b: local) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
              ?.map((local: local, index: number) => {
                const allSetsFromPlace: string[] = local?.conjuntosList?.map((cnj: conjunto) => cnj.id);
                return (
                  <LocaisAcordion
                    key={local.id}
                    allSetsFromPlace={allSetsFromPlace}
                    expanded={expandedLocal}
                    setExpanded={handleChangeExpand}
                    local={local}
                    conjuntosArray={conjuntosArray}
                    setConjuntosArray={setConjuntosArray}
                  />
                );
              })}
            {(client.locaisList?.length > 5 && !search)
              &&
              <Pagination
                boundaryCount={4}
                showFirstButton
                showLastButton
                count={count}
                size="small"
                page={page}
                variant='text'
                shape='circular'
                onChange={handleChange}
              />
            }
          </Stack>
        </AccordionDetails>
      </Accordion >
    </>
  );
};

const LocaisAcordion = ({
  local,
  conjuntosArray,
  setConjuntosArray,
  expanded,
  setExpanded,
  allSetsFromPlace
}: {
  local: local,
  conjuntosArray: string[]
  setConjuntosArray: Function,
  expanded: string | boolean,
  setExpanded: Function
  allSetsFromPlace: string[]
}) => {
  const [search, setSearch] = useState<string>('');
  const [paginated, setPaginated] = useState<local['conjuntosList']>([]);
  const [page, setPage] = useState(1);

  const PER_PAGE = 5;
  const count = Math.ceil(local.conjuntosList?.length / PER_PAGE);
  const _DATA = usePagination(local.conjuntosList?.sort((a, b) => { return a.nome > b.nome ? 1 : -1; }), PER_PAGE);

  useEffect(() => {
    search ? setPaginated(local.conjuntosList
      ?.filter((e: local['conjuntosList'][number]) => e.nome
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(search
          ?.normalize("NFD")
          ?.replace(/[\u0300-\u036f]/g, "")
          ?.toLowerCase()))) : setPaginated(_DATA.currentData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  const handleChange = (_e: ChangeEvent<unknown>, p: number) => {
    setPage(p);
    _DATA.jump(p);
  };

  function handleChangeClick(event: ChangeEvent<HTMLInputElement>) {
    const cnjs: string[] = [];
    if (event.target.checked) {
      local.conjuntosList?.forEach((cnj: conjunto) => {
        setConjuntosArray((prev: string[]) => prev ? [...prev, cnj.id] : [cnj.id]);
        cnjs?.push(cnj.id);
      });
    } else {
      local.conjuntosList.forEach((cnj: conjunto) => {
        setConjuntosArray((prev: string[]) => prev?.filter((el: string) => el !== cnj.id));
        cnjs.push(cnj.id);
      });
    }
  }

  return (
    <>
      <Accordion
        key={local.id}
        sx={{ width: '100%' }}
        expanded={expanded === local.id}
      >
        <AccordionSummary
          expandIcon={<ArrowDownwardIcon onClick={(isExpanded) => {
            trackEventMatomo('Gestão de formulários', 'click', 'accordeon', 'abre/fecha');
            if (expanded === local.id) {
              setExpanded(!isExpanded, '');
            } else
              setExpanded(true, local.id);
          }} />}
        >
          <Typography variant="h6">
            <FormGroup>
              <FormControlLabel
                control={<Checkbox
                  checked={allSetsFromPlace?.every(set => conjuntosArray?.includes(set)) || false}
                  indeterminate={allSetsFromPlace?.some(set => conjuntosArray
                    ?.includes(set))
                    && !allSetsFromPlace?.every(set => conjuntosArray?.includes(set))}
                  onChange={handleChangeClick}
                />}
                label={local.nome} />
            </FormGroup>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction='column' justifyContent='flex-start' alignItems='center' width='100%' spacing={1}>
            <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' width='100%' spacing={1}>
              <SearchInput search={search}
                setSearch={setSearch}
                arrayLenght={local.conjuntosList?.length}
                name={'Conjuntos:'} />
              <Typography>
                {paginated
                  ?.sort((a: conjunto, b: conjunto) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                  ?.map((conjunto: conjunto, index: number) => (
                    <Conjuntos
                      key={conjunto.id}
                      conjunto={conjunto}
                      conjuntosArray={conjuntosArray}
                      setConjuntosArray={setConjuntosArray}
                    />
                  ))}
              </Typography>
            </Stack>
            {(local.conjuntosList?.length > 5 && !search)
              &&
              <Pagination
                boundaryCount={4}
                showFirstButton
                showLastButton
                count={count}
                size="small"
                page={page}
                variant='text'
                shape='circular'
                onChange={handleChange}
              />
            }
          </Stack>
        </AccordionDetails>
      </Accordion >
    </>
  );
};

const Conjuntos = ({
  conjunto,
  conjuntosArray,
  setConjuntosArray,
}: {
  conjunto: conjunto,
  conjuntosArray: string[],
  setConjuntosArray: Function,
}) => {

  return (
    <FormGroup key={conjunto.id}>
      <FormControlLabel
        control={<Checkbox
          checked={conjuntosArray?.length > 0 && conjuntosArray?.includes(conjunto.id)}
          onChange={(event) => {
            const cnjs: string[] = [];
            if (event.target.checked) {
              setConjuntosArray((prev: string[]) => prev ? [...prev, conjunto.id] : [conjunto.id]);
              cnjs.push(conjunto.id);
            } else {
              setConjuntosArray((prev: string[]) => prev.filter((ele: string) => ele !== conjunto.id));
              cnjs.push(conjunto.id);
            }
          }}
        />} label={conjunto.nome} />
    </FormGroup>
  );
};

const SearchInput = ({
  search,
  setSearch,
  arrayLenght,
  name }: {
    search: string,
    setSearch: Function,
    arrayLenght: number,
    name: string
  }) => {

  return (
    <Stack
      width={'100%'}
      direction="row"
      justifyContent="flex-start"
      alignItems="flex-end"
      spacing={2}
      sx={{ marginBottom: '10px' }}
    >
      <Typography variant='subtitle1'>
        {name}
      </Typography>
      {arrayLenght > 5
        && <TextField
          size="small"
          fullWidth
          onClick={() => trackEventMatomo('modal associação de local', 'click', 'input', 'edita valor')}
          value={search}
          onChange={(eve) => setSearch(eve.target.value
            ?.normalize("NFD")
            ?.replace(/[\u0300-\u036f]/g, "")
            ?.toLowerCase())}
          label='Pesquisar'
        />}
    </Stack>
  );
};